import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Map as MapIcon,
  Ship,
  Radar,
  ClipboardList,
  FileText,
  ListChecks,
  Gauge,
  Sparkles,
  Settings as SettingsIcon,
  ShieldCheck,
  LifeBuoy,
  ChevronsLeft,
  ChevronsRight,
  type LucideIcon,
} from 'lucide-react';
import BrandLogo from '../brand/BrandLogo';
import { useAuth, type AppRole } from '../../context/AuthContext';
import { FLAGS } from '../../lib/featureFlags';
import { operatorApi } from '../../lib/api/operator';

export interface NavEntry {
  to: string;
  label: string;
  Icon: LucideIcon;
  /** Restrict to these roles (omit = any authenticated user). */
  roles?: AppRole[];
  /** Hide unless this feature flag is on. */
  flag?: keyof typeof FLAGS;
  end?: boolean;
}

interface NavGroup {
  label: string | null;
  items: NavEntry[];
}

/**
 * Global navigation model. One place decides what each persona sees:
 *  - Operators (role `viewer`, relabeled in UI) get the compliance surfaces.
 *  - Inspectors get their workspace + the vetting workflow.
 *  - Admin sees everything.
 *
 * Deliberately PLACES only — actions like "New inspection" live in the top
 * bar, and system items (settings/admin/help/demo) in the rail's foot strip.
 */
const GROUPS: NavGroup[] = [
  {
    label: 'Operate',
    items: [
      { to: '/workspace', label: 'My workspace', Icon: LayoutDashboard, roles: ['inspector', 'admin'] },
      { to: '/fleet', label: 'Fleet readiness', Icon: Radar, flag: 'fleet' },
      { to: '/map', label: 'Live map', Icon: MapIcon },
      { to: '/vessels', label: 'Vessels', Icon: Ship },
    ],
  },
  {
    label: 'Comply',
    items: [
      { to: '/actions', label: 'Corrective actions', Icon: ListChecks, flag: 'actions' },
      { to: '/tmsa', label: 'TMSA self-assessment', Icon: Gauge, flag: 'tmsa' },
      { to: '/vetting/inspections', label: 'Inspections', Icon: ClipboardList, roles: ['inspector', 'admin'] },
      { to: '/vetting/templates', label: 'Frameworks', Icon: FileText, roles: ['inspector', 'admin'] },
    ],
  },
];

/** System destinations — rendered as the compact strip at the rail's foot. */
const SYSTEM_ITEMS: NavEntry[] = [
  { to: '/vetting/demo', label: 'Product tour', Icon: Sparkles },
  { to: '/vetting/settings', label: 'Settings', Icon: SettingsIcon, roles: ['inspector', 'admin'] },
  { to: '/admin', label: 'Admin', Icon: ShieldCheck, roles: ['admin'] },
  { to: '/help', label: 'Help', Icon: LifeBuoy },
];

function allowed(n: NavEntry, role: AppRole | null): boolean {
  return (!n.roles || (role != null && n.roles.includes(role))) && (!n.flag || FLAGS[n.flag]);
}

export function visibleNavGroups(role: AppRole | null): NavGroup[] {
  return GROUPS.map((g) => ({
    label: g.label,
    items: g.items.filter((n) => allowed(n, role)),
  })).filter((g) => g.items.length > 0);
}

export function visibleSystemItems(role: AppRole | null): NavEntry[] {
  return SYSTEM_ITEMS.filter((n) => allowed(n, role));
}

/** Human label for a role — `viewer` is presented as Operator in the UI. */
export function roleLabel(role: AppRole | null): string {
  if (role === 'admin') return 'Admin';
  if (role === 'inspector') return 'Inspector';
  if (role === 'viewer') return 'Operator';
  return 'Guest';
}

/**
 * Fleet pulse — a live readiness summary at the top of the rail. Makes the
 * navigation itself answer the operator's first question ("how's the fleet?")
 * before they click anything. Shares react-query keys with the fleet
 * dashboard page, so it costs no extra requests once that page has loaded.
 */
function FleetPulse() {
  const { role } = useAuth();
  const eligible = FLAGS.fleet && (role === 'viewer' || role === 'admin');

  const { data: fleets } = useQuery({
    queryKey: ['fleets'],
    queryFn: () => operatorApi.listFleets(),
    staleTime: 60_000,
    enabled: eligible,
  });
  const fleet = fleets?.[0];
  const { data: dash } = useQuery({
    queryKey: ['fleet-dashboard', fleet?.id],
    queryFn: () => operatorApi.getFleetDashboard(fleet!.id),
    enabled: eligible && !!fleet?.id,
    staleTime: 30_000,
  });

  const navigate = useNavigate();
  if (!eligible || !dash || dash.stats.vessels === 0) return null;

  const avg = dash.stats.avg_readiness;
  const tone =
    avg == null ? { ring: '#475569', text: 'text-slate-400' }
    : avg >= 85 ? { ring: '#10B981', text: 'text-emerald-300' }
    : avg >= 65 ? { ring: '#06B6D4', text: 'text-cyan-300' }
    : avg >= 45 ? { ring: '#F59E0B', text: 'text-amber-300' }
    : { ring: '#EF4444', text: 'text-red-300' };
  const C = 2 * Math.PI * 15.5;
  const dash_ = avg == null ? 0 : (avg / 100) * C;

  return (
    <button
      onClick={() => navigate('/fleet')}
      className="mx-3 mt-3 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 p-3 flex items-center gap-3 text-left transition-colors group"
      title="Open fleet readiness"
    >
      <div className="relative w-11 h-11 shrink-0">
        <svg viewBox="0 0 38 38" className="w-full h-full -rotate-90">
          <circle cx="19" cy="19" r="15.5" fill="none" stroke="#1E293B" strokeWidth="3.5" />
          <circle
            cx="19" cy="19" r="15.5" fill="none"
            stroke={tone.ring} strokeWidth="3.5" strokeLinecap="round"
            strokeDasharray={`${dash_} ${C}`}
            className="transition-all duration-700"
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-[13px] font-bold tabular-nums ${tone.text}`}>
          {avg ?? '—'}
        </span>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fleet pulse</div>
        <div className="text-xs text-slate-300 mt-0.5 truncate">
          {dash.stats.vessels} vessels
          {dash.stats.open_actions > 0 && (
            <span className={dash.stats.overdue_actions > 0 ? 'text-red-300' : 'text-amber-300'}>
              {' '}· {dash.stats.open_actions} open
            </span>
          )}
          {dash.stats.open_actions === 0 && <span className="text-slate-500"> · all clear</span>}
        </div>
      </div>
    </button>
  );
}

interface SideNavProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/**
 * Desktop left rail. Collapsed mode shows icons only (used by default on the
 * live map so the chart keeps its full bleed).
 *
 * Design: the rail sits on a deeper surface (#070C18) than the content so the
 * app has depth; the active item carries a gradient icon tile + glow instead
 * of the generic border-left treatment; account/user chrome lives in the top
 * bar (AppShell), never down here where short viewports cut it off.
 */
export default function SideNav({ collapsed, onToggleCollapsed }: SideNavProps) {
  const { role } = useAuth();
  const navigate = useNavigate();
  const groups = visibleNavGroups(role);
  const system = visibleSystemItems(role);

  return (
    <aside
      // sticky + h-screen: the rail is ALWAYS exactly viewport height and
      // scrolls internally — it can never stretch with tall page content and
      // push its foot below the fold (the bug that used to hide the account
      // section at the bottom of the old rail).
      className={`hidden md:flex md:shrink-0 md:sticky md:top-0 md:h-screen flex-col border-r border-slate-800/70 bg-[#070C18] transition-all duration-base ease-brand ${
        collapsed ? 'md:w-16' : 'md:w-[232px]'
      }`}
    >
      {/* Brand row — collapse control lives up here, always reachable */}
      <div className={`h-14 shrink-0 border-b border-slate-800/70 flex items-center ${collapsed ? 'justify-center px-0' : 'justify-between pl-4 pr-2'}`}>
        {!collapsed && (
          <button onClick={() => navigate('/')} aria-label="Home" className="hover:opacity-80 active:scale-[0.98]">
            <BrandLogo size="sm" tone="dark" />
          </button>
        )}
        <button
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-slate-800/60 transition-colors"
        >
          {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && <FleetPulse />}

      <nav className="flex-1 px-3 py-3 space-y-5 overflow-y-auto">
        {groups.map((g, gi) => (
          <div key={g.label ?? gi}>
            {g.label && !collapsed && (
              <div className="px-2 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-600 font-bold">
                {g.label}
              </div>
            )}
            <div className="space-y-1">
              {g.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  title={collapsed ? n.label : undefined}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-2.5 rounded-lg text-[13px] font-medium transition-all duration-fast active:scale-[0.99] ${
                      collapsed ? 'justify-center px-0 py-2' : 'pl-2 pr-2.5 py-1.5'
                    } ${
                      isActive
                        ? 'text-white bg-slate-800/50'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-all duration-fast ${
                          isActive
                            ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-[0_0_14px_rgba(6,182,212,0.35)]'
                            : 'bg-slate-800/60 text-slate-400 group-hover:text-slate-200'
                        }`}
                      >
                        <n.Icon className="w-4 h-4" />
                      </span>
                      {!collapsed && <span className="truncate">{n.label}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* System strip — compact icon row, tooltips carry the labels */}
      <div className={`shrink-0 border-t border-slate-800/70 p-2 ${collapsed ? 'flex flex-col items-center gap-1' : 'flex items-center justify-around'}`}>
        {system.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            title={n.label}
            aria-label={n.label}
            className={({ isActive }) =>
              `p-2 rounded-lg transition-colors ${
                isActive ? 'text-cyan-300 bg-slate-800/60' : 'text-slate-500 hover:text-slate-200 hover:bg-slate-800/40'
              }`
            }
          >
            <n.Icon className="w-[17px] h-[17px]" />
          </NavLink>
        ))}
      </div>
    </aside>
  );
}
